import { useEffect, useState } from 'react';
import { AlertService } from '../../../../services/alert.service';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BlackListService } from '../../../../services/blacklist.service';
import { zone } from '../constants/data.constants';
import { useSession } from '../../../../hooks/use-session.hook';
import { PROFILES } from '../../../../constants';
import Select from 'react-select';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import Icon from '../../../../components/forms/icon.component';
import Title from '../../../../components/forms/titles.component';
import Switch from '../../../../components/forms/switch.component';
import DateHour from '../../../../components/forms/date-hour.component';
import TimePicker from '../../../../components/forms/time-picker.component';
import Tooltip from '../../../../components/forms/tooltip.component';

function Stepper3({ onValidationChange }: { onValidationChange: (isValid: boolean) => void }) {
  const { t } = useTranslation();
  const { session, selectedAccount } = useSession();
  const [diferenceHour, setDiferenceHour] = useState(false);
  const [diferenceHourDate, setDiferenceHourDate] = useState(false);
  const [finishBeforeInit, setFinishBeforeInit] = useState(false);
  const [lessThanOneHour, setLessThanOneHour] = useState(false);
  const [listBlacklist, setListBlacklist] = useState<any[]>([]);

  const errorMessage = diferenceHour
    ? 'La hora de inicio de campaña, debe ser igual a la hora que agendaste el envío de tu campaña.'
    : finishBeforeInit || lessThanOneHour
      ? 'Debe existir una diferencia mínima de una hora entre la Hora de Inicio y la Hora de Fin. Además, la Hora de Fin no puede ser igual o anterior a la Hora de Inicio.'
      : 'Verifica la hora agendada y la hora de inicio y fin de la dosificación de la campaña.';

  const {
    register,
    control,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext();

  const programing = watch('programing');
  const accountSelected = watch('account');
  const timeZone = watch('timeZone');
  const dateHour = watch('dateHour');
  const dosing = watch('dosing');
  const initHour = watch('initHour');
  const finishHour = watch('finishHour');
  const blacklistSwitch = watch('blacklistSwitch');
  const blacklist = watch('blacklist');

  const isProgramingValid = !programing || (timeZone && dateHour);
  const isDosingValid = !dosing || (initHour && finishHour);
  const isBlacklistValid = !blacklistSwitch || (blacklist && blacklist.length > 0);
  const hasCriticalError =
    finishBeforeInit || lessThanOneHour || diferenceHour || diferenceHourDate;

  const stepper3Valid = isProgramingValid && isDosingValid && isBlacklistValid && !hasCriticalError;

  const validateHour = async () => {
    const dateObj = new Date(dateHour);
    const dateHourHour = dateObj.getHours();
    const dateHourMinutes = dateObj.getMinutes();

    const initObj = new Date(`1970-01-01T${initHour}:00`);
    const initHourHour = initObj.getHours();
    const initHourMinutes = initObj.getMinutes();

    const totalMinutes = dateHourHour * 60 + dateHourMinutes;

    const minAllowed = 7 * 60;
    const maxAllowed = 22 * 60;

    if (programing) {
      if (totalMinutes < minAllowed || totalMinutes > maxAllowed) {
        setDiferenceHourDate(true);
      } else {
        setDiferenceHourDate(false);
      }
    }

    if (programing && dosing) {
      if (dateHourHour !== initHourHour || dateHourMinutes !== initHourMinutes) {
        setDiferenceHour(true);
      } else {
        setDiferenceHour(false);
      }
    } else {
      setDiferenceHour(false);
    }
  };

  const validateDosing = () => {
    if (initHour && finishHour) {
      const parseToDate = (hourStr: string) => {
        const [hour, minute] = hourStr.trim().split(':').map(Number);
        const date = new Date();
        date.setHours(hour, minute, 0, 0);
        return date;
      };

      const init = parseToDate(initHour);
      const finish = parseToDate(finishHour);

      const isFinishBeforeInit = finish <= init;
      const isLessThanOneHour = finish.getTime() - init.getTime() < 60 * 60 * 1000;

      setFinishBeforeInit(isFinishBeforeInit);
      setLessThanOneHour(isLessThanOneHour);
    } else {
      setFinishBeforeInit(false);
      setLessThanOneHour(false);
    }
  };

  const getBlackList = async () => {
    try {
      const response =
        session.profile === PROFILES.ADMIN
          ? await BlackListService.getBlackListByAccount(selectedAccount.account_id)
          : await BlackListService.getBlackListByAccount(accountSelected);

      const formatted = response
        .filter((item: any) => item.status === true)
        .map((item: any) => ({
          value: item._id,
          label: item.name,
          priority: item.priority
        }));
      setListBlacklist(formatted);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    if (accountSelected) {
      getBlackList();
    }
  }, [accountSelected]);

  useEffect(() => {
    validateHour();
  }, [initHour, dateHour, programing, dosing]);

  useEffect(() => {
    validateDosing();
  }, [initHour, finishHour]);

  useEffect(() => {
    const defaultPriorities = listBlacklist.filter((opt) => opt.priority).map((opt) => opt.value);

    if (defaultPriorities.length > 0 && getValues('blacklist')?.length === 0) {
      setValue('blacklist', defaultPriorities);
      setValue('blacklistSwitch', true);
      trigger('blacklist');
    }
  }, [listBlacklist]);

  useEffect(() => {
    onValidationChange(stepper3Valid);
  }, [
    programing,
    timeZone,
    dateHour,
    dosing,
    initHour,
    finishHour,
    blacklistSwitch,
    blacklist,
    diferenceHour,
    diferenceHourDate,
    finishBeforeInit,
    lessThanOneHour
  ]);

  return (
    <>
      <div className="text-left mb-10">
        <Title
          icon="ki-calendar-8"
          title={
            <>
              <span className="fw-bolder fw-bold fs-2">Programación</span>
              <Tooltip
                title={
                  'Si deseas enviar tu campaña de inmediato, no es necesario realizar ninguna acción adicional en este paso.'
                }
                className="d-inline-block cursor-pointer"
              >
                <Icon icon="ki-information-3" size="fs-1" className="ms-2" type="info" />
              </Tooltip>
            </>
          }
          subtitle={'Ve las diferentes configuraciones que tenemos para poder enviar tu campaña.'}
          type="info"
        />
      </div>
      <div className="fv-row mb-1">
        <div className="d-flex flex-stack cursor-pointer mb-3 ms-2">
          <div className="d-flex align-items-center me-2">
            <span className="symbol me-6 symbol-label">
              <Icon icon="ki-calendar" size="fs-3x" />
            </span>
            <div className="d-flex flex-column">
              <span className="fw-bolder fs-3">
                Agendar <span className="fs-7">({t('general.optional')})</span>
              </span>
              <span className="fs-6">Elige el día y la zona horaria para enviar tu campaña.</span>
            </div>
          </div>
          <div className="form-check form-check-custom form-check-solid">
            <Switch
              {...register('programing')}
              onChange={(e: any) => {
                const checked = e.target.checked;
                setValue('programing', checked);
                if (!checked) {
                  setValue('dateHour', '');
                  setValue('timeZone', '');
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="fv-row mb-3">
        {programing && (
          <div className="row mb-3">
            <div className="col-md-6 mb-3 animate-fadeInUp animate-delay-1 z-index-3">
              <Controller
                control={control}
                name="dateHour"
                rules={{ required: programing }}
                render={({ field }) => (
                  <DateHour
                    label={t('campaigns.date-time')}
                    requiredIndicator="required"
                    errors={errors.dateHour}
                    placeholder={t('campaigns.date-time-placeholder')}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
            <div className="col-md-6 mb-3 animate-fadeInUp animate-delay-2 z-index-4">
              <label className="form-label required">{t('campaigns.time-zone')}</label>
              <Controller
                control={control}
                name="timeZone"
                rules={{ required: programing }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      options={zone}
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                      classNamePrefix="form-select-custom"
                      placeholder={t('campaigns.time-zone-placeholder')}
                      onChange={(
                        selectedOption: {
                          value: string;
                          label: string;
                        } | null
                      ) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      value={
                        zone.find(
                          (option: { value: string; label: string }) => option.value === value
                        ) || null
                      }
                    />
                    <InvalidFeedback error={error} />
                  </>
                )}
              />
            </div>
          </div>
        )}
      </div>
      {diferenceHourDate && (
        <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-${index}">
          <Icon icon="ki-information-2" size="fs-2hx" type="info" />
          <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
            <h5 className="mb-1">¡ Atención !</h5>
            <span>
              No podemos iniciar tu campaña antes de las 07:00 hrs y déspues de las 22:00 hrs.
            </span>
          </div>
        </div>
      )}
      <div className="separator separator-dashed text-dark mb-5"></div>
      <div className="fv-row mb-1">
        <div className="d-flex flex-stack cursor-pointer mb-3 ms-2">
          <div className="d-flex align-items-center me-2">
            <span className="symbol me-6 symbol-label">
              <Icon icon="ki-time" size="fs-3x" />
            </span>
            <div className="d-flex flex-column">
              <span className="fw-bolder fs-3">
                Dosificar <span className="fs-7">({t('general.optional')})</span>
              </span>
              <span className="fs-6">Elige la hora de incio y fin de tu campaña.</span>
            </div>
          </div>
          <div className="form-check form-check-custom form-check-solid">
            <Switch
              {...register('dosing')}
              onChange={(e: any) => {
                const checked = e.target.checked;
                setValue('dosing', checked);
                if (!checked) {
                  setValue('initHour', '');
                  setValue('finishHour', '');
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="fv-row mb-3">
        {dosing && (
          <div className="row z-index-3">
            <div className="col-md-6 mb-7 animate-fadeInUp animate-delay-1 z-index-3">
              <Controller
                control={control}
                name="initHour"
                rules={{ required: dosing }}
                render={({ field }) => (
                  <TimePicker
                    requiredIndicator="required"
                    {...field}
                    label="Hora inicio"
                    placeholder="Selecciona una hora inicio"
                    time={field.value}
                    onChange={field.onChange}
                    disable={false}
                    errors={errors.initHour}
                  />
                )}
              />
            </div>
            <div className="col-md-6 mb-7 animate-fadeInUp animate-delay-2 z-index-3">
              <Controller
                control={control}
                name="finishHour"
                rules={{ required: dosing }}
                render={({ field }) => (
                  <TimePicker
                    requiredIndicator="required"
                    {...field}
                    label="Hora Final"
                    placeholder="Selecciona una hora Final"
                    time={field.value}
                    onChange={field.onChange}
                    disable={false}
                    errors={errors.initHour}
                  />
                )}
              />
            </div>
          </div>
        )}
      </div>
      {(diferenceHour || finishBeforeInit || (lessThanOneHour && !finishBeforeInit)) && (
        <div className="alert alert-dismissible bg-light-info border border-info d-flex border-dashed flex-column flex-sm-row p-5 mb-10 animate-fadeInUp animate-delay-1">
          <Icon icon="ki-information-2" size="fs-2hx" type="info" />
          <div className="d-flex flex-column pe-0 pe-sm-10 ms-5">
            <h5 className="mb-1">¡ Atención !</h5>
            <span>{errorMessage}</span>
          </div>
        </div>
      )}
      <div className="separator separator-dashed text-dark mb-5"></div>
      <div className="fv-row mb-1">
        <div className="d-flex flex-stack cursor-pointer mb-3 ms-2">
          <div className="d-flex align-items-center me-2">
            <span className="symbol me-6 symbol-label">
              <Icon icon="ki-shield" size="fs-3x" />
            </span>
            <div className="d-flex flex-column">
              <span className="fw-bolder fs-3">
                Lista Negra <span className="fs-7">({t('general.optional')})</span>
              </span>
              <span className="fs-6">
                Elige la lista de los números que no recibirán este mensaje.
              </span>
            </div>
          </div>
          <div className="form-check form-check-custom form-check-solid">
            <Switch
              {...register('blacklistSwitch')}
              onChange={(e: any) => {
                const checked = e.target.checked;
                setValue('blacklistSwitch', checked);
                if (!checked) {
                  setValue('blacklist', '');
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="fv-row mb-3">
        {blacklistSwitch && (
          <div className="row z-index-3">
            <label className="form-label required" htmlFor={'bl'}>
              Listas Negras
            </label>
            <div className="col-md-12 mb-7 animate-fadeInUp animate-delay-1 z-index-2">
              <Controller
                control={control}
                name="blacklist"
                rules={{ required: blacklistSwitch }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Select
                      id="bl"
                      options={listBlacklist}
                      isMulti
                      noOptionsMessage={({ inputValue }) =>
                        inputValue
                          ? t('general.no-options', { inputValue })
                          : t('general.no-options')
                      }
                      className={error ? 'form-select-custom is-invalid' : 'form-select-custom'}
                      classNamePrefix="form-select-custom"
                      placeholder={'Selecciona Lista negra'}
                      defaultValue={listBlacklist.filter((option) => option.priority)}
                      onChange={(selectedOptions) => {
                        const values = selectedOptions
                          ? selectedOptions.map((option) => option.value)
                          : [];
                        onChange(values);
                      }}
                      value={listBlacklist.filter((option) =>
                        Array.isArray(value) ? value.includes(option.value) : false
                      )}
                    />
                    <InvalidFeedback error={error} />
                  </>
                )}
              />
            </div>
            {listBlacklist.some((option) => option.priority) && (
              <div className="alert alert-dismissible bg-light-primary border border-primary border-dashed d-flex flex-column flex-sm-row p-5 mb-10">
                <Icon
                  icon="ki-shield-tick"
                  size="fs-3x"
                  type="primary"
                  className="me-4 mb-5 mb-sm-0"
                />
                <div className="d-flex flex-column pe-0 pe-sm-10">
                  <h5 className="mb-1">Tienes listas negras habilitadas como prioritarias:</h5>
                  <ul className="text-primary fw-bolder">
                    {listBlacklist
                      .filter((option) => option.priority)
                      .map((option, index) => (
                        <li key={index}>{option.label}</li>
                      ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default Stepper3;
